import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  FFU_AREA_KEYS,
  formatFfuNumber,
  formatFfuPrograms,
  formatFfuStoreyIndex,
  formatFfuStoredValue,
  type FfuApartment,
  type FfuAreaKey,
  type FfuBuilding,
  type FfuConnection,
  type FfuConnectionPoint,
  type FfuElementRequirement,
  type FfuOtherArea,
  type FfuOtherRequirement,
  type FfuOutput,
  type FfuPlan,
  type FfuRequirement,
  type FfuStorey,
} from '@shared/helpers/ffu-output-view.helpers';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    minWidth: 0,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    maxWidth: '68ch',
  },
  title: {
    margin: 0,
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.25,
  },
  location: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.4,
  },
  description: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.45,
  },
  building: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  sectionTitle: {
    margin: 0,
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.3,
  },
  caption: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: '0.04em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
  },
  factList: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    gap: theme.spacing(0.5, 2),
    margin: 0,
  },
  factTerm: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    letterSpacing: '0.04em',
    lineHeight: 1.4,
    textTransform: 'uppercase',
  },
  factValue: {
    margin: 0,
    fontSize: theme.typography.body2.fontSize,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  tableWrap: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  table: {
    width: 'max-content',
    minWidth: '100%',
    borderCollapse: 'collapse',
    fontSize: theme.typography.body2.fontSize,
    '& th, & td': {
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: 'top',
      whiteSpace: 'nowrap',
    },
    '& th': {
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      letterSpacing: '0.04em',
      textAlign: 'left',
      textTransform: 'uppercase',
      verticalAlign: 'bottom',
    },
    '& tbody tr:last-child td': {
      borderBottom: 'none',
    },
  },
  cellRight: {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    'th&': {
      textAlign: 'right',
    },
  },
  ground: {
    backgroundColor: theme.palette.action.hover,
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  empty: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
  },
}));

const AREA_LABEL: Record<FfuAreaKey, string> = {
  gfaM2: 'GFA',
  gfaAboveGroundM2: 'GFA above ground',
  livingAreaM2: 'Living area',
  premisesAreaM2: 'Premises area',
  footprintM2: 'Footprint',
};

const areaText = (value: number): string => `${formatFfuNumber(value)} m²`;

const metersText = (value: number): string => `${formatFfuNumber(value)} m`;

const blank = (value: string | undefined): string => value ?? '';

type Column<Row> = {
  id: string;
  label: string;
  align: 'left' | 'right';
  value: (row: Row) => string;
};

const StoredTable = <Row,>({
  caption,
  columns,
  rows,
  rowKey,
  ground,
}: {
  caption?: string;
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row, index: number) => string;
  ground?: (row: Row) => boolean;
}): React.ReactElement | null => {
  const { classes } = useStyles();
  if (rows.length === 0 || columns.length === 0) return null;
  return (
    <div className={classes.stack}>
      {caption != null && caption !== '' && (
        <p className={classes.caption}>{caption}</p>
      )}
      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  className={
                    column.align === 'right' ? classes.cellRight : undefined
                  }
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                className={ground?.(row) ? classes.ground : undefined}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={
                      column.align === 'right' ? classes.cellRight : undefined
                    }
                  >
                    {column.value(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const storeyColumns = (storeys: readonly FfuStorey[]): Column<FfuStorey>[] => {
  const columns: Column<FfuStorey>[] = [
    {
      id: 'index',
      label: 'Index',
      align: 'right',
      value: (row) =>
        row.index == null ? '' : formatFfuStoreyIndex(row.index),
    },
    {
      id: 'label',
      label: 'Plan',
      align: 'left',
      value: (row) => blank(row.label),
    },
    {
      id: 'gfa',
      label: 'GFA',
      align: 'right',
      value: (row) => (row.gfaM2 == null ? '' : areaText(row.gfaM2)),
    },
    {
      id: 'programs',
      label: 'Program',
      align: 'left',
      value: (row) => formatFfuPrograms(row.programs),
    },
    {
      id: 'innerHeight',
      label: 'Room height',
      align: 'right',
      value: (row) =>
        row.innerHeightM == null ? '' : metersText(row.innerHeightM),
    },
    {
      id: 'floorToFloor',
      label: 'Storey height',
      align: 'right',
      value: (row) =>
        row.floorToFloorHeightM == null
          ? ''
          : metersText(row.floorToFloorHeightM),
    },
    {
      id: 'fromGround',
      label: 'From ground',
      align: 'right',
      value: (row) =>
        row.elevationFromGroundM == null
          ? ''
          : metersText(row.elevationFromGroundM),
    },
  ];
  if (storeys.some((row) => row.planId != null)) {
    columns.push({
      id: 'planId',
      label: 'Plan sheet',
      align: 'left',
      value: (row) => blank(row.planId),
    });
  }
  if (storeys.some((row) => row.drawing != null)) {
    columns.push({
      id: 'drawing',
      label: 'Drawing',
      align: 'left',
      value: (row) => blank(row.drawing),
    });
  }
  return columns;
};

const isGroundStorey = (row: FfuStorey): boolean => row.index === 0;

const requirementText = (
  row: FfuRequirement,
  element: (row: FfuElementRequirement) => string,
): string => {
  switch (row.kind) {
    case 'element':
      return element(row);
    case 'other':
      return '';
    default:
      return assertUnreachable(row);
  }
};

const requirementColumns = (
  rows: readonly FfuRequirement[],
): Column<FfuRequirement>[] => {
  const columns: Column<FfuRequirement>[] = [
    {
      id: 'type',
      label: 'Type',
      align: 'left',
      value: (row) => {
        switch (row.kind) {
          case 'element':
            return 'element';
          case 'other':
            return row.type;
          default:
            return assertUnreachable(row);
        }
      },
    },
    {
      id: 'buildingId',
      label: 'Building',
      align: 'left',
      value: (row) => blank(row.buildingId),
    },
    {
      id: 'place',
      label: 'Place',
      align: 'left',
      value: (row) => requirementText(row, (element) => blank(element.place)),
    },
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      value: (row) => requirementText(row, (element) => blank(element.name)),
    },
    {
      id: 'share',
      label: 'Share',
      align: 'right',
      value: (row) =>
        requirementText(row, (element) =>
          element.share == null ? '' : formatFfuNumber(element.share),
        ),
    },
    {
      id: 'sbef',
      label: 'SBEF',
      align: 'left',
      value: (row) => requirementText(row, (element) => blank(element.sbef)),
    },
    {
      id: 'allowed',
      label: 'Allowed',
      align: 'left',
      value: (row) =>
        requirementText(row, (element) => element.allowed.join(', ')),
    },
  ];
  if (rows.some(isOtherRequirement)) {
    columns.push({
      id: 'stored',
      label: 'Stored',
      align: 'left',
      value: (row) => {
        switch (row.kind) {
          case 'element':
            return '';
          case 'other':
            return formatStored(row);
          default:
            return assertUnreachable(row);
        }
      },
    });
  }
  return columns;
};

const isOtherRequirement = (row: FfuRequirement): boolean => {
  switch (row.kind) {
    case 'other':
      return true;
    case 'element':
      return false;
    default:
      return assertUnreachable(row);
  }
};

const formatStored = (row: FfuOtherRequirement): string =>
  Object.entries(row.stored)
    .map(([key, value]) => `${key}: ${formatFfuStoredValue(value)}`)
    .join(', ');

const formatConnectionPoint = (point: FfuConnectionPoint): string =>
  [point.part, point.index == null ? undefined : formatFfuNumber(point.index)]
    .filter((part): part is string => part != null && part !== '')
    .join(' ');

const BuildingFacts: React.FC<{ building: FfuBuilding }> = ({ building }) => {
  const { classes } = useStyles();
  const facts: { label: string; value: string }[] = [];
  if (building.windowRatio?.value != null) {
    facts.push({
      label: 'Window ratio',
      value: formatFfuNumber(building.windowRatio.value),
    });
  }
  if (building.windowRatio?.determinedBy != null) {
    facts.push({
      label: 'Determined by',
      value: building.windowRatio.determinedBy,
    });
  }
  for (const key of FFU_AREA_KEYS) {
    const value = building.areas[key];
    if (value == null) continue;
    facts.push({ label: AREA_LABEL[key], value: areaText(value) });
  }
  if (building.slabThicknessMm != null) {
    facts.push({
      label: 'Slab thickness',
      value: `${formatFfuNumber(building.slabThicknessMm)} mm`,
    });
  }
  if (building.roof?.form != null) {
    facts.push({ label: 'Roof', value: building.roof.form });
  }
  if (building.roof?.pitchDeg != null) {
    facts.push({
      label: 'Pitch',
      value: `${formatFfuNumber(building.roof.pitchDeg)}°`,
    });
  }
  if (facts.length === 0) return null;
  return (
    <dl className={classes.factList}>
      {facts.map((fact) => (
        <React.Fragment key={fact.label}>
          <dt className={classes.factTerm}>{fact.label}</dt>
          <dd className={classes.factValue}>{fact.value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

const ConnectionFacts: React.FC<{ connection: FfuConnection }> = ({
  connection,
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.stack}>
      <h4 className={classes.caption}>Connection</h4>
      <dl className={classes.factList}>
        {connection.relationship != null && (
          <>
            <dt className={classes.factTerm}>Relationship</dt>
            <dd className={classes.factValue}>{connection.relationship}</dd>
          </>
        )}
        {connection.sharedStoreys.map((shared, index) => (
          <React.Fragment key={`${shared.label ?? 'shared'}-${index}`}>
            <dt className={classes.factTerm}>{shared.label ?? 'Shared'}</dt>
            <dd className={classes.factValue}>
              {shared.at.map(formatConnectionPoint).join(', ')}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

const APARTMENT_COLUMNS: readonly Column<FfuApartment>[] = [
  {
    id: 'label',
    label: 'Label',
    align: 'left',
    value: (row) => blank(row.label),
  },
  {
    id: 'count',
    label: 'Count',
    align: 'right',
    value: (row) => (row.count == null ? '' : formatFfuNumber(row.count)),
  },
  {
    id: 'area',
    label: 'Area',
    align: 'right',
    value: (row) => (row.areaM2 == null ? '' : areaText(row.areaM2)),
  },
  {
    id: 'min',
    label: 'Min',
    align: 'right',
    value: (row) => (row.areaMinM2 == null ? '' : areaText(row.areaMinM2)),
  },
  {
    id: 'max',
    label: 'Max',
    align: 'right',
    value: (row) => (row.areaMaxM2 == null ? '' : areaText(row.areaMaxM2)),
  },
];

const OTHER_AREA_COLUMNS: readonly Column<FfuOtherArea>[] = [
  {
    id: 'label',
    label: 'Label',
    align: 'left',
    value: (row) => blank(row.label),
  },
  {
    id: 'area',
    label: 'Area',
    align: 'right',
    value: (row) => (row.areaM2 == null ? '' : areaText(row.areaM2)),
  },
];

const planScale = (row: FfuPlan): string =>
  [row.scale?.ratio, row.scale?.paper]
    .filter((part) => part != null && part !== '')
    .join(' ');

const PLAN_COLUMNS: readonly Column<FfuPlan>[] = [
  { id: 'id', label: 'Id', align: 'left', value: (row) => blank(row.id) },
  { id: 'file', label: 'File', align: 'left', value: (row) => blank(row.file) },
  {
    id: 'page',
    label: 'Page',
    align: 'right',
    value: (row) => (row.page == null ? '' : formatFfuNumber(row.page)),
  },
  {
    id: 'label',
    label: 'Label',
    align: 'left',
    value: (row) => blank(row.label),
  },
  {
    id: 'storey',
    label: 'Storey',
    align: 'right',
    value: (row) =>
      row.storeyIndex == null ? '' : formatFfuStoreyIndex(row.storeyIndex),
  },
  {
    id: 'kind',
    label: 'Kind',
    align: 'left',
    value: (row) => blank(row.storeyKind),
  },
  { id: 'scale', label: 'Scale', align: 'left', value: planScale },
];

const requirementKey = (row: FfuRequirement, index: number): string => {
  switch (row.kind) {
    case 'element':
      return `element-${row.buildingId ?? ''}-${row.place ?? ''}-${row.sbef ?? ''}-${index}`;
    case 'other':
      return `other-${row.type}-${row.buildingId ?? ''}-${index}`;
    default:
      return assertUnreachable(row);
  }
};

const BuildingOutput: React.FC<{ building: FfuBuilding; index: number }> = ({
  building,
  index,
}) => {
  const { classes } = useStyles();
  const title = building.id ?? 'Building';
  return (
    <article
      className={classes.building}
      id={building.id == null ? undefined : `building-${building.id}`}
      aria-labelledby={`building-${building.id ?? index}-title`}
    >
      <h3
        className={classes.sectionTitle}
        id={`building-${building.id ?? index}-title`}
      >
        {title}
      </h3>
      <BuildingFacts building={building} />
      {building.parts.map((part, partIndex) => (
        <StoredTable
          key={`${part.name ?? 'part'}-${partIndex}`}
          caption={part.name ?? 'Part'}
          columns={storeyColumns(part.storeys)}
          rows={part.storeys}
          ground={isGroundStorey}
          rowKey={(row, rowIndex) =>
            `${part.name ?? 'part'}-${row.label ?? ''}-${rowIndex}`
          }
        />
      ))}
      {building.storeys.length > 0 && (
        <StoredTable
          caption="Storeys"
          columns={storeyColumns(building.storeys)}
          rows={building.storeys}
          ground={isGroundStorey}
          rowKey={(row, rowIndex) => `storey-${row.label ?? ''}-${rowIndex}`}
        />
      )}
      {building.connection != null && (
        <ConnectionFacts connection={building.connection} />
      )}
      {building.apartments.length > 0 && (
        <StoredTable
          caption="Apartments"
          columns={APARTMENT_COLUMNS}
          rows={building.apartments}
          rowKey={(row, rowIndex) => `${row.label ?? 'apartment'}-${rowIndex}`}
        />
      )}
      {building.otherAreas.length > 0 && (
        <StoredTable
          caption="Other areas"
          columns={OTHER_AREA_COLUMNS}
          rows={building.otherAreas}
          rowKey={(row, rowIndex) => `${row.label ?? 'area'}-${rowIndex}`}
        />
      )}
      {building.plans.length > 0 && (
        <StoredTable
          caption="Plans"
          columns={PLAN_COLUMNS}
          rows={building.plans}
          rowKey={(row, rowIndex) => `${row.id ?? 'plan'}-${rowIndex}`}
        />
      )}
      {building.requirements.length > 0 && (
        <div className={classes.stack}>
          <h4 className={classes.caption}>Requirements</h4>
          <StoredTable
            columns={requirementColumns(building.requirements)}
            rows={building.requirements}
            rowKey={requirementKey}
          />
        </div>
      )}
    </article>
  );
};

const hasVisibleOutput = (output: FfuOutput): boolean =>
  output.projectName != null ||
  output.description != null ||
  output.location != null ||
  output.buildings.length > 0;

/**
 * Stored FFU rows in the scorer document form. Requirement rows stay on the
 * building, with the fields a customer baseline reads.
 */
export const FfuOutputView: React.FC<{ output: FfuOutput }> = ({ output }) => {
  const { classes } = useStyles();
  if (!hasVisibleOutput(output)) {
    return <p className={classes.empty}>Nothing stored.</p>;
  }
  return (
    <div className={classes.root}>
      {(output.projectName != null ||
        output.location != null ||
        output.description != null) && (
        <header className={classes.header}>
          {output.projectName != null && (
            <h2 className={classes.title}>{output.projectName}</h2>
          )}
          {output.location != null && (
            <p className={classes.location}>{output.location}</p>
          )}
          {output.description != null && (
            <p className={classes.description}>{output.description}</p>
          )}
        </header>
      )}
      {output.buildings.map((building, index) => (
        <BuildingOutput
          key={`${building.id ?? 'building'}-${index}`}
          building={building}
          index={index}
        />
      ))}
    </div>
  );
};
